import { useStoreContext } from 'features/store';

import { ILocation } from '@rbi-ctg/frontend';
import { ServiceMode } from 'generated/graphql-gateway';
import { useDistanceText } from 'hooks/geolocation/use-distance-text';
import { useDismissAndReplace } from 'navigation/hooks/use-dismiss-and-navigate';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { routes } from 'utils/routing';
import SessionStorage, { SessionStorageKeys } from 'utils/session-storage';

import { UseStoreLocatorWithMapReturn } from '../types';

import { useStoreChangeDialog } from './use-store-change-dialog';

export const useStoreLocatorWithMap = (): UseStoreLocatorWithMapReturn => {
  const { deliveryAddress } = useOrderContext();
  const { serviceMode } = useServiceModeContext();
  const { noStoreSelected, store: restaurant } = useStoreContext();
  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const dismissAndReplace = useDismissAndReplace();

  const handleNavigateToStoreLocator = () => {
    dismissAndReplace({
      pathname: isDelivery ? routes.deliveryAddress : routes.storeLocator,
      params: {
        redirectTo: routes.cartCheckout,
      },
    });
    SessionStorage.setItem(SessionStorageKeys.HAS_SHOWN_STORE_CHANGE_DIALOG, true);
  };

  const storeChangeDialogProps = useStoreChangeDialog(handleNavigateToStoreLocator);

  const { distanceText, isLoading: isLoadingDistance, canShowDistance } = useDistanceText({
    restaurant,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  const addressLine1 = isDelivery
    ? deliveryAddress?.addressLine1 || ''
    : restaurant?.physicalAddress?.address1 || '';

  const cityStateZip = isDelivery
    ? `${deliveryAddress?.city || ''}, ${deliveryAddress?.state || ''} ${
        deliveryAddress?.zip || ''
      }`
    : `${restaurant?.physicalAddress?.city || ''}, ${
        restaurant?.physicalAddress?.stateProvince || ''
      } ${restaurant?.physicalAddress?.postalCode || ''}`;

  const restaurantCoordinates: ILocation | undefined =
    restaurant.latitude && restaurant.longitude
      ? {
          lat: restaurant.latitude,
          lng: restaurant.longitude,
        }
      : undefined;

  return {
    noStoreSelected,
    addressLine1,
    cityStateZip,
    handleChangeStore: storeChangeDialogProps.openDialog,
    isLoadingDistance,
    distanceText,
    canShowDistance,
    restaurantCoordinates,
    dialogProps: storeChangeDialogProps,
  };
};
